/**
 * Landmark → Database entry binding helpers (#172). The map owns
 * `entityFile`; entry pages discover reverse refs by scanning maps.
 */

import type { MapDocument, MapLandmark } from "./mapDocument";

export type MapLandmarkRef = {
  mapFile: string;
  layerId: string;
  landmarkId: string;
  /** Empty when the landmark has no caption. */
  caption: string;
};

function optionalTrimmed(
  value: MapLandmark["entityFile"] | MapLandmark["caption"],
): string {
  if (typeof value !== "string") return "";
  return value.trim();
}

/** Maps (and landmarks) that point at `entityFile` via `landmark.entityFile`. */
export function findMapLandmarkRefs(
  entityFile: string,
  maps: readonly { name: string; doc: MapDocument }[],
): MapLandmarkRef[] {
  const target = entityFile.trim();
  if (target.length === 0) return [];
  const refs: MapLandmarkRef[] = [];
  for (const map of maps) {
    for (const layer of map.doc.layers) {
      for (const landmark of layer.landmarks) {
        const bound = optionalTrimmed(landmark.entityFile);
        if (bound !== target) continue;
        refs.push({
          mapFile: map.name,
          layerId: layer.id,
          landmarkId: landmark.id,
          caption: optionalTrimmed(landmark.caption),
        });
      }
    }
  }
  return refs.sort((a, b) => {
    const byMap = a.mapFile.localeCompare(b.mapFile, "en", {
      sensitivity: "base",
    });
    if (byMap !== 0) return byMap;
    return a.caption.localeCompare(b.caption, "en", { sensitivity: "base" });
  });
}
